import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { ELEMENT_CONFIG } from '../../utils/elementConfig';

const BudgetSetup = ({ projectId, floors, onUpdate }) => {
  const [selectedFloor, setSelectedFloor] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    element_category: '',
    element_detail: '',
    budgeted_manhours_forming: '',
    budgeted_manhours_pouring: '',
    budgeted_manhours_stripping: '',
    budgeted_manhours_punch: '',
    budgeted_cubic_yards: '',
    notes: ''
  });
  const [editingBudget, setEditingBudget] = useState(null);

  useEffect(() => {
    if (selectedFloor) {
      fetchBudgets();
    }
  }, [selectedFloor]);

  useEffect(() => {
  if (floors.length > 0 && !selectedFloor) {
    setSelectedFloor(floors[0].id.toString());
  }
}, [floors]);

  const fetchBudgets = async () => {
    try {
      const response = await api.get(`/floors/${selectedFloor}/budgets`);
      setBudgets(response.data);
    } catch (error) {
      console.error('Error fetching budgets:', error);
    }
  };

  const calculateTotal = () => {
    const forming = parseFloat(formData.budgeted_manhours_forming) || 0;
    const pouring = parseFloat(formData.budgeted_manhours_pouring) || 0;
    const stripping = parseFloat(formData.budgeted_manhours_stripping) || 0;
    const punch = parseFloat(formData.budgeted_manhours_punch) || 0;
    return forming + pouring + stripping + punch;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        element_category: formData.element_category,
        element_detail: formData.element_detail,
        budgeted_manhours_forming: parseFloat(formData.budgeted_manhours_forming) || 0,
        budgeted_manhours_pouring: parseFloat(formData.budgeted_manhours_pouring) || 0,
        budgeted_manhours_stripping: parseFloat(formData.budgeted_manhours_stripping) || 0,
        budgeted_manhours_punch: parseFloat(formData.budgeted_manhours_punch) || 0,
        budgeted_cubic_yards: formData.budgeted_cubic_yards ? parseFloat(formData.budgeted_cubic_yards) : null,
        notes: formData.notes
      };

      if (editingBudget) {
        await api.put(`/budgets/${editingBudget.id}`, payload);
      } else {
        await api.post(`/floors/${selectedFloor}/budgets`, payload);
      }
      
      setShowForm(false);
      setEditingBudget(null);
      setFormData({
        element_category: '',
        element_detail: '',
        budgeted_manhours_forming: '',
        budgeted_manhours_pouring: '',
        budgeted_manhours_stripping: '',
        budgeted_manhours_punch: '',
        budgeted_cubic_yards: '',
        notes: ''
      });
      fetchBudgets();
      onUpdate();
    } catch (error) {
      console.error('Error saving budget:', error);
      alert('Failed to save budget');
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      element_category: budget.element_category,
      element_detail: budget.element_detail,
      budgeted_manhours_forming: budget.budget.forming || '',
      budgeted_manhours_pouring: budget.budget.pouring || '',
      budgeted_manhours_stripping: budget.budget.stripping || '',
      budgeted_manhours_punch: budget.budget.punch || '',
      budgeted_cubic_yards: budget.budgeted_cubic_yards || '',
      notes: budget.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (budgetId) => {
    if (window.confirm('Delete this budget?')) {
      try {
        await api.delete(`/budgets/${budgetId}`);
        fetchBudgets();
        onUpdate();
      } catch (error) {
        console.error('Error deleting budget:', error);
        alert('Failed to delete budget');
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBudget(null);
    setFormData({
      element_category: '',
      element_detail: '',
      budgeted_manhours_forming: '',
      budgeted_manhours_pouring: '',
      budgeted_manhours_stripping: '',
      budgeted_manhours_punch: '',
      budgeted_cubic_yards: '',
      notes: ''
    });
  };

  if (floors.length === 0) {
    return (
      <div className="card">
        <p>Please add floors first before setting up budgets.</p>
      </div>
    );
  }

  return (
    <div className="budget-setup">
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="form-group">
          <label>Select Floor</label>
          <select
            value={selectedFloor}
            onChange={(e) => setSelectedFloor(e.target.value)}
            style={{ padding: '10px', fontSize: '14px' }}
          >
            <option value="">Choose a floor...</option>
            {floors.map(floor => (
              <option key={floor.id} value={floor.id}>
                {floor.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedFloor && (
        <>
          <div style={{ marginBottom: '20px', marginLeft: '2rem' }}>
            <button onClick={() => setShowForm(true)} className="primary">
              + Add Budget Item
            </button>
          </div>

          {showForm && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3>{editingBudget ? 'Edit Budget' : 'New Budget Item'}</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Element Category *</label>
                  <select
                    value={formData.element_category}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      element_category: e.target.value,
                      element_detail: ''
                    })}
                    required
                  >
                    <option value="">Select Category</option>
                    {Object.keys(ELEMENT_CONFIG).map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Element Detail *</label>
                  <select
                    value={formData.element_detail}
                    onChange={(e) => setFormData({ ...formData, element_detail: e.target.value })}
                    disabled={!formData.element_category}
                    required
                  >
                    <option value="">Select Detail</option>
                    {formData.element_category && ELEMENT_CONFIG[formData.element_category].map(detail => (
                      <option key={detail} value={detail}>{detail}</option>
                    ))}
                  </select>
                </div>

                {/* PHASE BUDGETS */}
                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                  <h4 style={{ marginBottom: '15px' }}>Budget by Phase (Manhours)</h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                      <label>Forming</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.budgeted_manhours_forming}
                        onChange={(e) => setFormData({ ...formData, budgeted_manhours_forming: e.target.value })}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Pouring</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.budgeted_manhours_pouring}
                        onChange={(e) => setFormData({ ...formData, budgeted_manhours_pouring: e.target.value })}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Stripping</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.budgeted_manhours_stripping}
                        onChange={(e) => setFormData({ ...formData, budgeted_manhours_stripping: e.target.value })}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>Punch Out</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.budgeted_manhours_punch}
                        onChange={(e) => setFormData({ ...formData, budgeted_manhours_punch: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div style={{ 
                    marginTop: '15px', 
                    padding: '10px', 
                    background: '#fff', 
                    borderRadius: '5px',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    Total Budget: {calculateTotal().toFixed(1)} MH
                  </div>
                </div>

                <div className="form-group">
                  <label>Budgeted Cubic Yards</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.budgeted_cubic_yards}
                    onChange={(e) => setFormData({ ...formData, budgeted_cubic_yards: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="submit" className="primary">Save</button>
                  <button type="button" onClick={handleCancel} className="secondary">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            <h3>Budget Items for {floors.find(f => f.id === parseInt(selectedFloor))?.name}</h3>
            {budgets.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
        <table>
                    <thead>
                        <tr>
                        <th rowSpan="2">Element</th>
                        <th colSpan="5" style={{ textAlign: 'center', background: '#5698a8ff' }}>Budget (MH)</th>
                        <th colSpan="5" style={{ textAlign: 'center', background: '#027474ff' }}>Actual (MH)</th>
                        <th rowSpan="2">Budget CY</th>
                        <th rowSpan="2">Actual CY</th>
                        <th rowSpan="2">CY Variance</th>
                        <th rowSpan="2">MH Variance</th>
                        <th rowSpan="2">% Complete</th>
                        <th rowSpan="2">Actions</th>
                        </tr>
                        <tr>
                        <th style={{ background: '#5698a8ff' }}>Form</th>
                        <th style={{ background: '#5698a8ff' }}>Pour</th>
                        <th style={{ background: '#5698a8ff' }}>Strip</th>
                        <th style={{ background: '#5698a8ff' }}>Punch</th>
                        <th style={{ background: '#5698a8ff' }}>Total</th>
                        <th style={{ background: '#027474ff' }}>Form</th>
                        <th style={{ background: '#027474ff' }}>Pour</th>
                        <th style={{ background: '#027474ff' }}>Strip</th>
                        <th style={{ background: '#027474ff' }}>Punch</th>
                        <th style={{ background: '#027474ff' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {budgets.map(budget => (
                        <tr key={budget.id}>
                            <td>
                            <div style={{ fontWeight: 'bold' }}>{budget.element_detail}</div>
                            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{budget.element_category}</div>
                            </td>
                            {/* Budget MH columns */}
                            <td>{budget.budget.forming}</td>
                            <td>{budget.budget.pouring}</td>
                            <td>{budget.budget.stripping}</td>
                            <td>{budget.budget.punch}</td>
                            <td><strong>{budget.budget.total}</strong></td>
                            {/* Actual MH columns */}
                            <td>{budget.actual.forming}</td>
                            <td>{budget.actual.pouring}</td>
                            <td>{budget.actual.stripping}</td>
                            <td>{budget.actual.punch}</td>
                            <td><strong>{budget.actual.total}</strong></td>
                            {/* CY columns */}
                            <td>{budget.budgeted_cubic_yards || '-'}</td>
                            <td>{budget.actual_cubic_yards}</td>
                            <td style={{ 
                            color: (budget.actual_cubic_yards - (budget.budgeted_cubic_yards || 0)) > 0 ? '#e74c3c' : '#27ae60',
                            fontWeight: 'bold'
                            }}>
                            {budget.budgeted_cubic_yards ? 
                                `${((budget.actual_cubic_yards - budget.budgeted_cubic_yards) > 0 ? '+' : '')}${(budget.actual_cubic_yards - budget.budgeted_cubic_yards).toFixed(1)}` 
                                : '-'}
                            </td>
                            {/* MH Variance */}
                            <td style={{ 
                            color: budget.variance.total > 0 ? '#e74c3c' : '#27ae60',
                            fontWeight: 'bold'
                            }}>
                            {budget.variance.total > 0 ? '+' : ''}{budget.variance.total}
                            <div style={{ fontSize: '11px' }}>
                                ({budget.variance_percent > 0 ? '+' : ''}{budget.variance_percent}%)
                            </div>
                            </td>
                            <td>{budget.percent_complete}%</td>
                            <td>
                            <button 
                                onClick={() => handleEdit(budget)} 
                                className="secondary"
                                style={{ marginRight: '5px', padding: '4px 8px', fontSize: '12px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => handleDelete(budget.id)} 
                                className="danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                                Delete
                            </button>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                    </table>
              </div>
            ) : (
              <p>No budget items for this floor yet.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BudgetSetup;